import { emailPIIReg } from './regExp';
type MaskFunction = (value: string, autoPopulate?: boolean) => string;

/**
 * maskPhone
 * @param phone
 * @param hasAreaCode
 * @returns
 */
export const maskPhone = (phone: string, hasAreaCode?: boolean): string => {
  if (!phone) return phone;
  const normalized = phone.replace(/^\+/, '');
  const target = hasAreaCode ? normalized : phone;
  const len = target.length;

  if (hasAreaCode) {
    if (len === 13) {
      return target.replace(/^(\d{5})(\d{4})(\d{4})$/, '$1****$3');
    }
    if (len === 11) {
      console.log('target', target);

      return target.replace(/^(\d{6})(\d{4})(\d{1})$/, '$1****$3');
    }
    if (len > 4) {
      const visibleStart = Math.floor(len / 2);
      const start = target.slice(0, visibleStart);
      const end = target.slice(-1);
      return `${start}****${end}`;
    }
  }
  return len === 11
    ? target.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1****$3')
    : target.replace(/^(\d{3,6})(\d{4})(\d{1,6})$/, '$1****$3');
};

/**
 * maskfirstName
 * @param firstName
 * @returns
 */
export const maskFirstName = (firstName: string): string => {
  if (!firstName) return firstName;

  const len = firstName.length;

  if (len === 1) return '***';
  if (len === 2) return firstName[0] + '***';

  return firstName[0] + '***' + firstName[len - 1];
};

/**
 * masklastName
 * @param lastName
 * @returns
 */
export const maskLastName = (lastName: string): string => {
  if (!lastName) return lastName;
  if (lastName.length === 1) return lastName;
  return lastName[0] + '***';
};

/**
 * maskemail
 * @param email
 * @returns
 */
export const maskEmail = (email: string) => {
  if (!email) return email;
  return email.replace(emailPIIReg, '$1***$2');
};

/**
 * maskAddressLine
 * @param address
 * @returns
 */
export const maskAddressLine = (address: string): string => {
  if (!address) return address;
  const len = address.length;
  if (len === 1) return '*';
  if (len === 2) return address[0] + '*';
  return address[0] + '*'.repeat(len - 2) + address[len - 1];
};

/**
 * maskFullAdress
 * @param address1
 * @param address2
 * @param fullAddress
 * @returns
 */
export const maskFullAdress = (
  address1: string,
  address2: string,
  fullAddress: string,
): string => {
  if (!fullAddress) return fullAddress;
  const maskedAddress1 = address1 ? maskAddressLine(address1) : '';
  const maskedAddress2 = address2 ? maskAddressLine(address2) : '';

  let maskFullAdress = fullAddress;
  if (fullAddress.includes(address1)) {
    maskFullAdress = maskFullAdress.replace(address1, maskedAddress1);
  }
  if (fullAddress.includes(address2)) {
    maskFullAdress = maskFullAdress.replace(address2, maskedAddress2);
  }

  return maskFullAdress;
};

/**
 * maskUnknownFullName
 * @param name
 * @returns
 */
export const maskUnknownFullName = (name: string): string => {
  if (!name) return name;
  if (name.length <= 2) return name.replace(/.(?=.)/g, '*');
  return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1];
};

export const maskThirdPartyAccount = (account: string): string => {
  if (!account) return account;
  const len = account.length;
  if (len <= 4) return '*'.repeat(len);
  return account.slice(0, len - 4) + '*'.repeat(4);
};

/**
 * maskFullName 通过后端返回进行字符串匹配判断前后
 * @param lastName
 * @param firstName
 * @param fullName
 * @returns
 */
export const maskFullName = (
  lastName: string,
  firstName: string,
  fullName: string,
): string => {
  if (!lastName || !firstName || !fullName)
    return maskUnknownFullName(fullName);

  lastName = lastName.trim();
  firstName = firstName.trim();
  fullName = fullName.trim();

  const maskedLastName = maskLastName(lastName);
  const maskedFirstName = maskFirstName(firstName);

  let maskedName = fullName;

  if (fullName.includes(lastName)) {
    maskedName = maskedName.replace(lastName, maskedLastName);
  }

  if (fullName.includes(firstName)) {
    maskedName = maskedName.replace(firstName, maskedFirstName);
  }

  if (maskedName === fullName) {
    return maskUnknownFullName(fullName);
  }

  return maskedName;
};

/**
 * maskConfigFullName 可以根据portal配置判断前后
 * @param name
 * @param fullNameAutoPopulate
 * @returns
 */
export const maskConfigFullName = (
  name: string,
  fullNameAutoPopulate?: { firstNameFirst: boolean; separator: string },
): string => {
  if (!name) return name;

  const trimmedName = name.trim();

  const separator = trimmedName.includes(' ')
    ? ' '
    : trimmedName.includes(',')
    ? ','
    : null;

  if (!separator) {
    return maskUnknownFullName(trimmedName);
  }

  const [first, ...rest] = trimmedName.split(separator);
  const second = rest.join(separator).trim();

  const firstMasked =
    fullNameAutoPopulate?.firstNameFirst ?? true
      ? maskFirstName(first.trim())
      : maskLastName(first.trim());

  const secondMasked =
    fullNameAutoPopulate?.firstNameFirst ?? true
      ? maskLastName(second)
      : maskFirstName(second);

  return `${firstMasked}${separator}${secondMasked}`;
};

/**
 * maskFieldValue
 * @param fieldName
 * @param fieldValue
 * @param fullNameAutoPopulate
 * @returns
 */
export const maskFieldValue = (
  fieldName: string,
  fieldValue: string,
  fullNameAutoPopulate?: { firstNameFirst: boolean; separator: string },
): string => {
  if (fieldValue === '-') return fieldValue;
  const maskers: Record<string, MaskFunction> = {
    Email: maskEmail,
    'Last Name': maskLastName,
    'First Name': maskFirstName,
    'Mobile Number': (v) => maskPhone(v, true),
    'Full Name': (value) => maskConfigFullName(value, fullNameAutoPopulate),
    'Cross-border Name': (value) =>
      maskConfigFullName(value, fullNameAutoPopulate),
  };

  const masker = maskers[fieldName];
  return masker ? masker(fieldValue) : fieldValue;
};

/**
 * maskFullNameWithPatternAndConfig 通过后端返回进行字符串匹配，若没有匹配上则使用配置
 * @param lastName
 * @param firstName
 * @param fullName
 * @returns
 */
export const maskFullNameWithPatternAndConfig = (
  lastName: string,
  firstName: string,
  fullName: string,
  fullNameAutoPopulate?: { firstNameFirst: boolean; separator: string },
): string => {
  if (!lastName || !firstName || !fullName)
    return maskConfigFullName(fullName, fullNameAutoPopulate);

  lastName = lastName.trim();
  firstName = firstName.trim();
  fullName = fullName.trim();

  const maskedLastName = maskLastName(lastName);
  const maskedFirstName = maskFirstName(firstName);

  let maskedName = fullName;

  if (fullName.includes(lastName)) {
    maskedName = maskedName.replace(lastName, maskedLastName);
  }

  if (fullName.includes(firstName)) {
    maskedName = maskedName.replace(firstName, maskedFirstName);
  }
  console.log('maskConfigFullName', maskedName);

  if (maskedName === fullName) {
    return maskConfigFullName(fullName, fullNameAutoPopulate);
  }

  return maskedName;
};
